import { useContext, useMemo, type FC } from "react";
import { Navigate, useParams } from "react-router-dom";
import SetPageLoading from "src/components/layouts/SetPageLoading";
import { Permission } from "src/api/Tmd.dtos";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import AuthenticatedUserContext from "src/contexts/authentication/AuthenticatedUserContext";
import hasAnyPermission from "src/utilities/hasAnyPermission";

export const TrialInstitutionDetailsPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return (
        <Navigate to={`/trials/${trialId}/institutions/${trialInstitutionId}/details`} replace />
    );
};

export const TrialInstitutionParticipantsPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/participants`}
            replace
        />
    );
};

export const TrialInstitutionParticipantPageRedirect: FC = () => {
    const { trialId, trialInstitutionId, patientId } = useParams();

    if (!trialId || !trialInstitutionId || !patientId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/participants/${patientId}/details`}
            replace
        />
    );
};

export const TrialParticipantPageRedirect: FC = () => {
    const { trialId, patientId } = useParams();

    if (!trialId || !patientId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/participants/${patientId}/details`}
            replace
        />
    );
};

export const InstitutionDepartmentPageRedirect: FC = () => {
    const { institutionId, departmentId } = useParams();

    if (!institutionId || !departmentId) {
        return null;
    }

    return (
        <Navigate
            to={`/institutions/${institutionId}/departments/${departmentId}/details`}
            replace
        />
    );
};

export const TrialInstitutionItemsPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return <Navigate to={`/trials/${trialId}/institutions/${trialInstitutionId}/items`} replace />;
};

export const TrialInstitutionItemPageRedirect: FC = () => {
    const { trialId, trialInstitutionId, trialInstitutionItemId } = useParams();

    if (!trialId || !trialInstitutionId || !trialInstitutionItemId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/items/${trialInstitutionItemId}`}
            replace
        />
    );
};

export const TrialInstitutionPersonnelPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return (
        <Navigate to={`/trials/${trialId}/institutions/${trialInstitutionId}/personnel`} replace />
    );
};

export const TrialInstitutionPersonnelDetailsPageRedirect: FC = () => {
    const { trialId, trialInstitutionId, personnelRoleId } = useParams();

    if (!trialId || !trialInstitutionId || !personnelRoleId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/personnel/${personnelRoleId}`}
            replace
        />
    );
};

export const TrialInstitutionNotesPageRedirect: FC = () => {
    const { trialId, trialInstitutionId } = useParams();

    if (!trialId || !trialInstitutionId) {
        return null;
    }

    return <Navigate to={`/trials/${trialId}/institutions/${trialInstitutionId}/notes`} replace />;
};

export const TrialInstitutionNotePageRedirect: FC = () => {
    const { trialId, trialInstitutionId, noteId } = useParams();

    if (!trialId || !trialInstitutionId || !noteId) {
        return null;
    }

    return (
        <Navigate
            to={`/trials/${trialId}/institutions/${trialInstitutionId}/notes/${noteId}`}
            replace
        />
    );
};

export const generalSettingsPermissions = [
    Permission.RolesView,
    Permission.CountriesView,
    Permission.StatesView,
    Permission.ItemComponentTypesView,
    Permission.NoteTypesView,
    Permission.GendersView,
    Permission.InterestsView,
    Permission.MembershipTypesView,
    Permission.ProfessionalDisciplinesView,
    Permission.SalutationsView,
];

export const trialSettingsPermissions = [
    Permission.TumorGroupsView,
    Permission.AllocatedWPsView,
    Permission.TrialPhasesView,
    Permission.InvolvementsView,
    Permission.TrialTypesView,
];

export const itemSettingsPermissions = [
    Permission.TemplateTrialItemsView,
    Permission.TrialInstitutionItemTemplatesView,
    Permission.ItemComponentsView,
    Permission.CategoriesView,
];

export const institutionSettingsPermissions = [
    Permission.SiteLocationsView,
    Permission.KeyContactsView,
    Permission.FacilityAllianceMembersView,
    Permission.InstitutionTypesView,
    Permission.InstitutionCodingView,
];

export const qaSettingsPermissions = [
    Permission.FormTemplatesView,
    Permission.FormComponentsView,
    Permission.ReportingTagsView,
    Permission.ReportingTagCategoriesView,
];

export const ocUserRoleSettingsPermissions = [Permission.OpenClinicaUserRolesView];

export const hubSpotSettingsPermissions = [Permission.HubspotContactExportView];

export const SettingsPageRedirect: FC = () => {
    const { data: user, loading: userLoading } = useContext(AuthenticatedUserContext);

    const permissions = user?.permissions ?? [];

    const loading = userLoading;

    const redirect = useMemo(() => {
        if (loading) {
            return null;
        }

        if (hasAnyPermission(permissions, generalSettingsPermissions)) {
            return <Navigate to="/settings/general" replace />;
        } else if (hasAnyPermission(permissions, trialSettingsPermissions)) {
            return <Navigate to="/settings/trial" replace />;
        } else if (hasAnyPermission(permissions, itemSettingsPermissions)) {
            return <Navigate to="/settings/item" replace />;
        } else if (hasAnyPermission(permissions, institutionSettingsPermissions)) {
            return <Navigate to="/settings/institution" replace />;
        } else if (hasAnyPermission(permissions, qaSettingsPermissions)) {
            return <Navigate to="/settings/qa" replace />;
        } else if (hasAnyPermission(permissions, ocUserRoleSettingsPermissions)) {
            return <Navigate to="/settings/openclinicauserroles" replace />;
        } else if (hasAnyPermission(permissions, hubSpotSettingsPermissions)) {
            return <Navigate to="/settings/hubspot" replace />;
        } else {
            return <Navigate to="/" replace />;
        }
    }, [loading, permissions]);

    return (
        <>
            <SetPageLoading loading={!redirect} />
            {redirect}
        </>
    );
};

export const GeneralSettingsPageRedirect: FC = () => {
    const { data: user, loading: userLoading } = useContext(AuthenticatedUserContext);

    const permissions = user?.permissions ?? [];

    const loading = userLoading;

    const redirect = useMemo(() => {
        if (loading) {
            return null;
        }

        if (hasAnyPermission(permissions, [Permission.RolesView])) {
            return <Navigate to="/settings/general/roles" replace />;
        } else if (hasAnyPermission(permissions, [Permission.CountriesView])) {
            return <Navigate to="/settings/general/countries" replace />;
        } else if (hasAnyPermission(permissions, [Permission.StatesView])) {
            return <Navigate to="/settings/general/states" replace />;
        } else if (hasAnyPermission(permissions, [Permission.ItemComponentTypesView])) {
            return <Navigate to="/settings/general/itemcomponenttypes" replace />;
        } else if (hasAnyPermission(permissions, [Permission.NoteTypesView])) {
            return <Navigate to="/settings/general/notetypes" replace />;
        } else if (hasAnyPermission(permissions, [Permission.GendersView])) {
            return <Navigate to="/settings/general/genders" replace />;
        } else if (hasAnyPermission(permissions, [Permission.InterestsView])) {
            return <Navigate to="/settings/general/interests" replace />;
        } else if (hasAnyPermission(permissions, [Permission.MembershipTypesView])) {
            return <Navigate to="/settings/general/membershiptypes" replace />;
        } else if (hasAnyPermission(permissions, [Permission.ProfessionalDisciplinesView])) {
            return <Navigate to="/settings/general/professionaldisciplines" replace />;
        } else if (hasAnyPermission(permissions, [Permission.SalutationsView])) {
            return <Navigate to="/settings/general/salutations" replace />;
        } else {
            return <Navigate to="/settings" replace />;
        }
    }, [loading, permissions]);

    return (
        <>
            <SetPageLoading loading={!redirect} />
            {redirect}
        </>
    );
};

export const TrialSettingsPageRedirect: FC = () => {
    const { data: user, loading: userLoading } = useContext(AuthenticatedUserContext);

    const permissions = user?.permissions ?? [];

    const loading = userLoading;

    const redirect = useMemo(() => {
        if (loading) {
            return null;
        }

        if (hasAnyPermission(permissions, [Permission.TrialCategoriesView])) {
            return <Navigate to="/settings/trial/trialcategorys" replace />;
        } else if (hasAnyPermission(permissions, [Permission.TumorGroupsView])) {
            return <Navigate to="/settings/trial/tumourgroups" replace />;
        } else if (hasAnyPermission(permissions, [Permission.AllocatedWPsView])) {
            return <Navigate to="/settings/trial/allocatedwps" replace />;
        } else if (hasAnyPermission(permissions, [Permission.TrialPhasesView])) {
            return <Navigate to="/settings/trial/trialphases" replace />;
        } else if (hasAnyPermission(permissions, [Permission.InvolvementsView])) {
            return <Navigate to="/settings/trial/involvements" replace />;
        } else if (hasAnyPermission(permissions, [Permission.TrialAnalysisTypesView])) {
            return <Navigate to="/settings/trial/trialanalysistypes" replace />;
        } else if (hasAnyPermission(permissions, [Permission.TrialPresentationTypesView])) {
            return <Navigate to="/settings/trial/trialPresentationTypes" replace />;
        } else if (hasAnyPermission(permissions, [Permission.TrialPublicationTypesView])) {
            return <Navigate to="/settings/trial/trialPublicationTypes" replace />;
        }
        // else if (hasAnyPermission(permissions, [ Permission.TemplateTrialItemsView ])) {
        //     return <Navigate to="/settings/trial/templatetrialitems" replace />;
        // }
        else {
            return <Navigate to="/settings" replace />;
        }
    }, [loading, permissions]);

    return (
        <>
            <SetPageLoading loading={!redirect} />
            {redirect}
        </>
    );
};

export const QaSettingsPageRedirect: FC = () => {
    const { data: user, loading: userLoading } = useContext(AuthenticatedUserContext);

    const permissions = user?.permissions ?? [];

    const loading = userLoading;

    const redirect = useMemo(() => {
        if (loading) {
            return null;
        }

        if (hasAnyPermission(permissions, [Permission.FormTemplatesView])) {
            return <Navigate to="/settings/qa/formtemplates" replace />;
        } else if (hasAnyPermission(permissions, [Permission.FormComponentsView])) {
            return <Navigate to="/settings/qa/formcomponents" replace />;
        } else if (hasAnyPermission(permissions, [Permission.ReportingTagsView])) {
            return <Navigate to="/settings/qa/reportingtags" replace />;
        } else if (hasAnyPermission(permissions, [Permission.ReportingTagCategoriesView])) {
            return <Navigate to="/settings/qa/reportingtagcategories" replace />;
        } else if (hasAnyPermission(permissions, [Permission.TaskTypesView])) {
            return <Navigate to="/settings/qa/tasktypes" replace />;
        } else if (hasAnyPermission(permissions, [Permission.FormTemplateFileTypesView])) {
            return <Navigate to="/settings/qa/formtemplatefiletypes" replace />;
        } else if (hasAnyPermission(permissions, [Permission.QAEmailTemplatesView])) {
            return <Navigate to="/settings/qa/qaEmailTemplates" replace />;
        } else {
            return <Navigate to="/settings" replace />;
        }
    }, [loading, permissions]);

    return (
        <>
            <SetPageLoading loading={!redirect} />
            {redirect}
        </>
    );
};

export const ItemSettingsPageRedirect: FC = () => {
    const { data: user, loading: userLoading } = useContext(AuthenticatedUserContext);

    const permissions = user?.permissions ?? [];

    const loading = userLoading;

    const redirect = useMemo(() => {
        if (loading) {
            return null;
        }

        if (hasAnyPermission(permissions, [Permission.TemplateTrialItemsView])) {
            return <Navigate to="/settings/item/trialitemtemplates" replace />;
        } else if (hasAnyPermission(permissions, [Permission.TrialInstitutionItemTemplatesView])) {
            return <Navigate to="/settings/item/itemtemplates" replace />;
        } else if (hasAnyPermission(permissions, [Permission.ItemComponentsView])) {
            return <Navigate to="/settings/item/itemcomponents" replace />;
        } else if (hasAnyPermission(permissions, [Permission.CategoriesView])) {
            return <Navigate to="/settings/item/itemcategories" replace />;
        } else {
            return <Navigate to="/settings" replace />;
        }
    }, [loading, permissions]);

    return (
        <>
            <SetPageLoading loading={!redirect} />
            {redirect}
        </>
    );
};

export const HubspotSettingsPageRedirect: FC = () => {
    const { data: user, loading: userLoading } = useContext(AuthenticatedUserContext);

    const permissions = user?.permissions ?? [];

    const loading = userLoading;

    const redirect = useMemo(() => {
        if (loading) {
            return null;
        }

        if (hasAnyPermission(permissions, [Permission.HubspotContactExportView])) {
            return <Navigate to="/settings/hubspot/activemembers" replace />;
        } else {
            return <Navigate to="/settings" replace />;
        }
    }, [loading, permissions]);

    return (
        <>
            <SetPageLoading loading={!redirect} />
            {redirect}
        </>
    );
};

export const InstitutionSettingsPageRedirect: FC = () => {
    const { data: user, loading: userLoading } = useContext(AuthenticatedUserContext);

    const permissions = user?.permissions ?? [];

    const loading = userLoading;

    const redirect = useMemo(() => {
        if (loading) {
            return null;
        }

        if (hasAnyPermission(permissions, [Permission.SiteLocationsView])) {
            return <Navigate to="/settings/institution/sitelocations" replace />;
        } else if (hasAnyPermission(permissions, [Permission.FacilityAllianceMembersView])) {
            return <Navigate to="/settings/institution/facilityalliancemembers" replace />;
        } else if (hasAnyPermission(permissions, [Permission.InstitutionTypesView])) {
            return <Navigate to="/settings/institution/institutiontypes" replace />;
        } else if (hasAnyPermission(permissions, [Permission.InstitutionCodingView])) {
            return <Navigate to="/settings/institution/institutioncodings" replace />;
        } else {
            return <Navigate to="/settings" replace />;
        }
    }, [loading, permissions]);

    return (
        <>
            <SetPageLoading loading={!redirect} />
            {redirect}
        </>
    );
};
